// File: src/components/OmniCine/Timeline/Hologram/TimelineHologramBridge.tsx
//
// TimelineHologramBridge (Phase 1 — A3-safe) + SurfaceIntent v1 Emitter (Phase 12.3)
// -----------------------------------------------------------------------------
// Purpose:
// - Consume hologram overlay intents from the Preview surface and translate them
//   into safe timeline-side effects.
// - Deterministic application: coalesce incoming intents and apply ONLY on the
//   next animation frame boundary.
// - Listener-only: does not require reducer changes.
//
// NEW (Phase 12.3):
// - SurfaceIntent v1 emission (OmniCine → OmniCast):
//     listen "omnicine:surface:intent" (and optional "omnicine:holo:intent" kind=surface/intent)
//     emit "omnicast:surface:intent" with witness-safe payload.
//
// -----------------------------------------------------------------------------
//
// Listens:
// - "omnicine:holo:intent"  detail: { kind, atFrame, atTimeSec, payload }
// - "timeline:seek"         detail: { timeSec, source? }
// - "omnicine:surface:intent" detail: { suggestMode?, overlays?, grade?, intensity01?, sessionId?, source? }
//
// Emits (debug + observability):
// - window.__oc_debug.holoTimeline.*
// - "omnicine:timeline:holo:applied" detail: { kind, ok, timeSec, source, from, via? }
//
// NEW emits:
// - "omnicast:surface:intent" detail: SurfaceIntent v1 (witness-safe)
//
// -----------------------------------------------------------------------------


import React, { useEffect, useMemo, useRef } from "react";
import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type HoloIntent = {
  kind: string;
  atFrame?: number;
  atTimeSec?: number;
  payload?: Record<string, unknown>;
};

type SeekDetail = {
  timeSec: number;
  source?: unknown;
};

type SurfaceMode = "focus" | "balanced" | "ambient";

type SurfaceIntentDetail = {
  sessionId?: string;
  suggestMode?: SurfaceMode;
  overlays?: Partial<Record<"hud" | "ghosts" | "telemetry" | "sensors" | "provenance", boolean>>;
  grade?: {
    warmth01?: number;
    contrast01?: number;
    vignette01?: number;
    bloom01?: number;
    grain01?: number;
  };
  intensity01?: number;
  ts?: number;
  source?: string;
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function safeMode(v: unknown, fallback: SurfaceMode): SurfaceMode {
  const s = String(v ?? "").toLowerCase().trim();
  return s === "focus" || s === "balanced" || s === "ambient" ? (s as SurfaceMode) : fallback;
}

function safeBool(v: unknown): boolean | undefined {
  return typeof v === "boolean" ? v : undefined;
}

function safe01(v: unknown): number | undefined {
  return Number.isFinite(v as number) ? clamp01(Number(v)) : undefined;
}

function normalizeSource(raw: unknown): "user" | "xr-user" | "ai-assist" | "holo" {
  const s = String(raw ?? "holo").toLowerCase().trim();
  if (s === "xr-user") return "xr-user";
  if (s === "ai-assist") return "ai-assist";
  if (s === "user") return "user";
  return "holo";
}

/**
 * Installs a singleton window listener by key.
 * Removes any previous listener registered under that key first.
 * Prevents duplicate handlers across remounts / Vite HMR.
 */
function installSingletonListener(key: string, type: string, handler: EventListener) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = window as any;
  w.__oc_debug = w.__oc_debug || {};
  w.__oc_debug.__listeners = w.__oc_debug.__listeners || {};

  const prev = w.__oc_debug.__listeners[key] as EventListener | undefined;
  if (prev) window.removeEventListener(type, prev);

  w.__oc_debug.__listeners[key] = handler;
  window.addEventListener(type, handler);

  return () => {
    const cur = w.__oc_debug?.__listeners?.[key];
    if (cur === handler) {
      window.removeEventListener(type, handler);
      delete w.__oc_debug.__listeners[key];
    }
  };
}

function bucket01(x: number, steps = 20) {
  const k = Math.max(1, Math.floor(steps));
  return Math.round(clamp01(x) * k) / k;
}

function getSessionIdFallback(): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w: any = window as any;
  const sid =
    (typeof w?.__oc_debug?.sessionId === "string" && w.__oc_debug.sessionId) ||
    (typeof w?.__oc_debug?.boundSessionId === "string" && w.__oc_debug.boundSessionId) ||
    undefined;
  return sid ?? undefined;
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function TimelineHologramBridge(): JSX.Element | null {
  const { state, dispatch } = useOmniCineTimeline();
  const { durationSec } = state;

  // Always-fresh state access
  const stateRef = useRef(state);
  stateRef.current = state;

  // Pending seek (coalesced) + rAF apply gate
  const pendingSeekRef = useRef<{ timeSec: number; source: string; from: string } | null>(null);
  const rafRef = useRef<number>(0);

  // Cache the first working dispatch candidate
  const workingDispatchKeyRef = useRef<string | null>(null);

  // SurfaceIntent v1 emission dedupe/throttle
  const lastIntentSigRef = useRef<string | null>(null);
  const lastIntentTsRef = useRef<number>(0);

  const dispatchCandidates = useMemo(() => {
    return [
      {
        key: "timeline/setPlayhead:playheadSec",
        action: (timeSec: number, source: string) =>
          ({
            type: "timeline/setPlayhead",
            payload: { playheadSec: timeSec },
            meta: { source, note: "hologram seek" },
          }) as any,
      },
      {
        key: "timeline/setPlayheadSec:timeSec",
        action: (timeSec: number, source: string) =>
          ({
            type: "timeline/setPlayheadSec",
            payload: { timeSec },
            meta: { source, note: "hologram seek" },
          }) as any,
      },
      {
        key: "transport/seek:timeSec",
        action: (timeSec: number, source: string) =>
          ({
            type: "transport/seek",
            payload: { timeSec },
            meta: { source, note: "hologram seek" },
          }) as any,
      },
      {
        key: "playhead/set:timeSec",
        action: (timeSec: number, source: string) =>
          ({
            type: "playhead/set",
            payload: { timeSec },
            meta: { source, note: "hologram seek" },
          }) as any,
      },
    ];
  }, []);

  const recordDebug = (patch: Record<string, unknown>) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: any = window as any;
    w.__oc_debug = w.__oc_debug || {};
    w.__oc_debug.holoTimeline = w.__oc_debug.holoTimeline || {};
    Object.assign(w.__oc_debug.holoTimeline, patch);
    return w.__oc_debug.holoTimeline as Record<string, unknown>;
  };

  const safeDispatchSeek = (timeSec: number, sourceRaw: string) => {
    const max = Number(stateRef.current?.durationSec ?? durationSec ?? 0) || 0;
    const t = clamp(timeSec, 0, max);

    recordDebug({
      seekRequested: (Number((window as any).__oc_debug?.holoTimeline?.seekRequested) || 0) + 1,
      lastSeekRequested: { timeSec: t, source: sourceRaw },
    });

    // cached candidate
    const cachedKey = workingDispatchKeyRef.current;
    if (cachedKey) {
      const hit = dispatchCandidates.find((c) => c.key === cachedKey);
      if (hit) {
        try {
          dispatch(hit.action(t, sourceRaw));
          recordDebug({
            seekApplied: (Number((window as any).__oc_debug?.holoTimeline?.seekApplied) || 0) + 1,
            lastSeekApplied: { timeSec: t, source: sourceRaw, via: cachedKey },
            seekUnsupported: false,
          });
          return { ok: true as const, via: cachedKey };
        } catch {
          workingDispatchKeyRef.current = null;
        }
      } else {
        workingDispatchKeyRef.current = null;
      }
    }

    // probe candidates
    for (const cand of dispatchCandidates) {
      try {
        dispatch(cand.action(t, sourceRaw));
        workingDispatchKeyRef.current = cand.key;

        recordDebug({
          seekApplied: (Number((window as any).__oc_debug?.holoTimeline?.seekApplied) || 0) + 1,
          lastSeekApplied: { timeSec: t, source: sourceRaw, via: cand.key },
          seekUnsupported: false,
        });

        return { ok: true as const, via: cand.key };
      } catch {
        // try next
      }
    }

    // fallback event
    recordDebug({
      seekUnsupported: true,
      lastSeekUnsupported: { timeSec: t, source: sourceRaw },
    });

    window.dispatchEvent(
      new CustomEvent("omnicine:timeline:seek", {
        detail: { timeSec: t, source: sourceRaw, reason: "no known reducer action" },
      }),
    );

    return { ok: false as const, via: null as string | null };
  };

  const scheduleApply = () => {
    if (rafRef.current) return;

    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;

      const p = pendingSeekRef.current;
      pendingSeekRef.current = null;
      if (!p) return;

      const res = safeDispatchSeek(p.timeSec, p.source);

      window.dispatchEvent(
        new CustomEvent("omnicine:timeline:holo:applied", {
          detail: {
            kind: "seek",
            ok: res.ok,
            timeSec: p.timeSec,
            source: p.source,
            from: p.from,
            via: res.via ?? undefined,
          },
        }),
      );
    });
  };

  /* ------------------------------------------------------------------ */
  /* SurfaceIntent v1 emitter (OmniCine → OmniCast)                       */
  /* ------------------------------------------------------------------ */

  const emitSurfaceIntent = (raw: SurfaceIntentDetail) => {
    const now = Date.now();
    const minDt = 125; // ~8Hz ceiling

    const suggestMode = raw.suggestMode != null ? safeMode(raw.suggestMode, "balanced") : undefined;

    const overlaysIn = raw.overlays ?? {};
    const overlays = {
      ...(safeBool(overlaysIn.hud) != null ? { hud: safeBool(overlaysIn.hud) } : {}),
      ...(safeBool(overlaysIn.ghosts) != null ? { ghosts: safeBool(overlaysIn.ghosts) } : {}),
      ...(safeBool(overlaysIn.telemetry) != null ? { telemetry: safeBool(overlaysIn.telemetry) } : {}),
      ...(safeBool(overlaysIn.sensors) != null ? { sensors: safeBool(overlaysIn.sensors) } : {}),
      ...(safeBool(overlaysIn.provenance) != null ? { provenance: safeBool(overlaysIn.provenance) } : {}),
    };

    const gradeIn = raw.grade ?? {};
    const grade = {
      ...(safe01(gradeIn.warmth01) != null ? { warmth01: bucket01(safe01(gradeIn.warmth01)!, 20) } : {}),
      ...(safe01(gradeIn.contrast01) != null ? { contrast01: bucket01(safe01(gradeIn.contrast01)!, 20) } : {}),
      ...(safe01(gradeIn.vignette01) != null ? { vignette01: bucket01(safe01(gradeIn.vignette01)!, 20) } : {}),
      ...(safe01(gradeIn.bloom01) != null ? { bloom01: bucket01(safe01(gradeIn.bloom01)!, 20) } : {}),
      ...(safe01(gradeIn.grain01) != null ? { grain01: bucket01(safe01(gradeIn.grain01)!, 20) } : {}),
    };

    const intensity01 = raw.intensity01 != null ? bucket01(safe01(raw.intensity01) ?? 0, 20) : undefined;

    const sessionId = (typeof raw.sessionId === "string" && raw.sessionId.trim()) ? raw.sessionId.trim() : getSessionIdFallback();
    const source = String(raw.source ?? "omnicine").trim() || "omnicine";

    // Dedupe signature
    const sig = JSON.stringify({
      sessionId: sessionId ?? null,
      suggestMode: suggestMode ?? null,
      overlays,
      grade,
      intensity01: intensity01 ?? null,
      source,
    });

    const prevSig = lastIntentSigRef.current;
    const prevTs = lastIntentTsRef.current;

    const okTime = now - prevTs >= minDt;
    if (prevSig === sig && !okTime) return;

    lastIntentSigRef.current = sig;
    lastIntentTsRef.current = now;

    const detail = {
      ...(sessionId ? { sessionId } : {}),
      ...(suggestMode ? { suggestMode } : {}),
      ...(Object.keys(overlays).length ? { overlays } : {}),
      ...(Object.keys(grade).length ? { grade } : {}),
      ...(intensity01 != null ? { intensity01 } : {}),
      ts: now,
      source,
    };

    // Emit OmniCast witness-safe intent (string literal to avoid OmniCast imports in OmniCine)
    window.dispatchEvent(new CustomEvent("omnicast:surface:intent", { detail }));

    // Debug mirror
    recordDebug({
      surfaceIntentCount: (Number((window as any).__oc_debug?.holoTimeline?.surfaceIntentCount) || 0) + 1,
      lastSurfaceIntent: detail,
    });
  };

  useEffect(() => {
    // Initialize debug bucket
    recordDebug({
      bridgeMountedAt: typeof performance !== "undefined" ? performance.now() : Date.now(),
      intentCount: (window as any).__oc_debug?.holoTimeline?.intentCount ?? 0,
      seekRequested: (window as any).__oc_debug?.holoTimeline?.seekRequested ?? 0,
      seekApplied: (window as any).__oc_debug?.holoTimeline?.seekApplied ?? 0,
      surfaceIntentCount: (window as any).__oc_debug?.holoTimeline?.surfaceIntentCount ?? 0,
    });

    const onHoloIntent = (e: Event) => {
      const detail = (e as CustomEvent).detail as HoloIntent | undefined;
      if (!detail || typeof detail.kind !== "string") return;

      // Count + snapshot
      const prev = Number((window as any).__oc_debug?.holoTimeline?.intentCount) || 0;
      recordDebug({
        intentCount: prev + 1,
        lastIntent: detail,
      });

      // Existing: preview seek
      if (detail.kind === "preview/seek") {
        const raw = detail.payload?.seekToSec;
        const t = Number(raw);
        if (!Number.isFinite(t)) return;

        pendingSeekRef.current = {
          timeSec: t,
          source: "holo",
          from: "omnicine:holo:intent",
        };
        scheduleApply();
        return;
      }

      // NEW: SurfaceIntent via holo intent (optional)
      if (detail.kind === "surface/intent") {
        const p = (detail.payload ?? {}) as any;
        emitSurfaceIntent({
          sessionId: typeof p.sessionId === "string" ? p.sessionId : undefined,
          suggestMode: p.suggestMode,
          overlays: p.overlays,
          grade: p.grade,
          intensity01: p.intensity01,
          source: p.source ?? "holo",
        });
      }
    };

    const onTimelineSeek = (e: Event) => {
      const d = (e as CustomEvent).detail as SeekDetail | undefined;
      const t = Number(d?.timeSec);
      if (!Number.isFinite(t)) return;

      pendingSeekRef.current = {
        timeSec: t,
        source: normalizeSource(d?.source),
        from: "timeline:seek",
      };
      scheduleApply();
    };

    // NEW: primary SurfaceIntent input (OmniCine UI dispatches this)
    const onSurfaceIntent = (e: Event) => {
      const d = (e as CustomEvent).detail as SurfaceIntentDetail | undefined;
      if (!d) return;
      emitSurfaceIntent(d);
    };

    // Singleton bindings
    const c1 = installSingletonListener("holoIntent", "omnicine:holo:intent", onHoloIntent as EventListener);
    const c2 = installSingletonListener("holoSeek", "timeline:seek", onTimelineSeek as EventListener);
    const c3 = installSingletonListener("surfaceIntent", "omnicine:surface:intent", onSurfaceIntent as EventListener);

    return () => {
      c1();
      c2();
      c3();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [dispatch, durationSec]);

  return null;
}
